<!--
  Quote Item Data Tree Component Template

  This template renders a single quote item and its children in a tree structure.
  The component supports drag-and-drop and recursive rendering of nested children.
-->
<form [formGroup]="form">
  @let dragging = dragging$ | async;
  <!-- Main Item Row: Grid layout with conditional styling based on expansion state -->
  <div
    class="border border-surface-switch-300 items-center bg-surface-switch-50"
    [class.ms-4]="depth > 0"
    [class.bg-surface-switch-100]="expanded"
  >
    <div
      class="grid gap-2"
      [ngClass]="
        tradeStudyToolFeaturesEnabled
          ? 'grid-cols-[25px_1.2fr_75px_0.5fr_55px_1.3fr_80px_1.8fr_0.9fr_40px_110px]'
          : 'grid-cols-[25px_1.2fr_75px_0.5fr_55px_1.3fr_80px_70px_0.9fr_1fr_40px_110px]'
      "
    >
      <div
        pDraggable
        (onDragStart)="quoteItemDataEditorService.dragStart(item)"
        (onDragEnd)="quoteItemDataEditorService.dragEnd()"
        class="flex justify-center items-center cursor-grab px-4 hover:bg-primary-100"
      >
        <i
          class="pi pi-ellipsis-v hover:bg-primary-100 transition-colors rounded p-1"
        ></i>
      </div>
      <div
        class="grid grid-cols-subgrid items-center"
        [class.col-span-11]="!tradeStudyToolFeaturesEnabled"
        [class.col-span-10]="tradeStudyToolFeaturesEnabled"
        [class.dragging-item]="
          dragging && dragging.persistent_id == item.persistent_id
        "
      >
        <!-- Name (1.2fr) -->
        <div
          class="font-semibold flex items-center gap-2 overflow-hidden min-w-0"
        >
          <input
            pInputText
            type="text"
            formControlName="name"
            class="w-full border-0 bg-transparent p-0 font-semibold"
            [placeholder]="'Item ' + ((item.sibling_order || 0) + 1)"
          />
        </div>
        <!-- Quantity (75px) -->
        <div class="flex items-center text-muted-color">
          @if (selectedPricingModelHasOutputQuantity) {
            <!-- Quantity is calculated by pricing model when output_quantity is enabled -->
            <span
              class="text-sm text-muted-color italic"
              pTooltip="Quantity will be calculated by the pricing model"
              tooltipPosition="top"
              >Auto</span
            >
          } @else {
            <input
              pInputText
              type="text"
              formControlName="quantity"
              class="w-full border-0 bg-transparent p-0 text-xs"
              [class.text-muted-color]="!form.get('quantity')?.value"
              [placeholder]="
                form.get('quantity_type')?.value == QuantityType.HOURLY
                  ? 'hh:mm:ss'
                  : 'Qty'
              "
              [invalid]="
                form.get('quantity')?.invalid && form.get('quantity')?.touched
              "
            />
          }
        </div>
        <!-- Quantity Type (0.5fr) -->
        <div class="flex items-center text-muted-color overflow-hidden min-w-0">
          @if (children.length > 0) {
            <span class="text-xs text-muted-color">—</span>
          } @else if (!selectedPricingModelHasOutputQuantity) {
            <p-select
              formControlName="quantity_type"
              [options]="quantityTypeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="—"
              class="w-full"
              [style]="{ 'font-size': '0.75rem' }"
              appendTo="body"
            />
          } @else {
            <span class="text-sm text-muted-color italic">Auto</span>
          }
        </div>
        <!-- Batch Size (55px) -->
        <div class="flex items-center text-muted-color">
          <p-inputNumber
            formControlName="batch_size"
            [inputStyle]="{ width: '100%', textAlign: 'left' }"
            [min]="0"
            [max]="999999"
            [maxFractionDigits]="0"
            [minFractionDigits]="0"
            placeholder="—"
          />
        </div>
        <!-- Pricing Model (1.3fr) -->
        <div
          class="flex items-center gap-1 text-muted-color overflow-hidden min-w-0"
        >
          @if (children.length > 0) {
            <span class="text-xs text-muted-color">—</span>
          } @else {
            <p-select
              formControlName="pricing_model_id"
              [options]="pricingModelService.selectOptions()"
              optionLabel="name"
              optionValue="id"
              placeholder="Manual"
              class="min-w-0 flex-1 text-xs"
              [style]="{ 'font-size': '0.75rem', width: '100%' }"
              appendTo="body"
              [filter]="true"
              filterBy="name"
            />
            @if (unmatchedPricingModelSuggestion; as pricingSuggestion) {
              <i
                class="pi pi-info-circle text-sm text-muted-color shrink-0"
                [pTooltip]="pricingSuggestion"
                tooltipPosition="top"
                [attr.aria-label]="pricingSuggestion"
              ></i>
            }
            @if (selectedPricingModel) {
              <i
                class="pi pi-info-circle text-sm text-green-500 cursor-pointer hover:bg-surface-switch-200 transition-colors rounded p-1 shrink-0"
                pTooltip="View and edit pricing parameters"
                tooltipPosition="top"
                aria-label="View and edit pricing parameters"
                (click)="$event.stopPropagation(); onOpenPricingParameters()"
              ></i>
            }
          }
        </div>
        <!-- Unit Cost (80px) -->
        <div class="flex items-center gap-1 text-muted-color min-w-0">
          @if (children.length > 0) {
            <span class="text-xs text-muted-color">—</span>
          } @else if (
            isManualPricing || !selectedPricingModelHasOutputUnitCost
          ) {
            <!-- User can set unit_cost when:
                   - Manual pricing is selected
                   - Pricing model does not output unit_cost
              -->
            <p-inputNumber
              class="min-w-0 flex-1"
              formControlName="unit_cost"
              [inputStyle]="{ width: '100%', textALign: 'left' }"
              [min]="0"
              [max]="9999999"
              [mode]="'currency'"
              [currency]="currencyCode || 'GBP'"
              locale="en-US"
            />
            @if (needsHourlyRateInput) {
              <i
                class="pi pi-exclamation-triangle text-orange-500 text-sm shrink-0"
                pTooltip="Check or enter hourly rate"
                tooltipPosition="top"
                aria-label="Check or enter hourly rate"
              ></i>
            }
          } @else {
            <span class="text-sm text-muted-color italic">Auto</span>
          }
        </div>
        @if (!tradeStudyToolFeaturesEnabled) {
          <!-- Markup Percentage (70px) -->
          <div class="flex items-center text-muted-color">
            @if (children.length > 0) {
              <span class="text-xs text-muted-color">—</span>
            } @else {
              <p-inputNumber
                formControlName="markup_percentage"
                mode="decimal"
                [inputStyle]="{ width: '100%', textAlign: 'left' }"
                [min]="-999"
                [max]="999"
                [minFractionDigits]="0"
                [maxFractionDigits]="2"
              >
              </p-inputNumber>
            }
          </div>
          <!-- Tax Code (0.9fr) -->
          <div
            class="flex items-center text-muted-color overflow-hidden min-w-0"
          >
            @if (children.length > 0) {
              <span class="text-xs text-muted-color">—</span>
            } @else {
              <app-tax-code-select
                formControlName="tax_code_id"
                [isNewItem]="isNewItem"
                class="w-full"
              />
            }
          </div>
        }
        <!-- Material (1fr) -->
        <div
          class="flex items-center gap-1 text-muted-color overflow-hidden min-w-0"
        >
          <app-material-select
            formControlName="material_id"
            class="min-w-0 flex-1 w-full"
          />
          @if (unmatchedMaterialSuggestion; as materialSuggestion) {
            <i
              class="pi pi-info-circle text-sm text-muted-color shrink-0"
              [pTooltip]="materialSuggestion"
              tooltipPosition="top"
              [attr.aria-label]="materialSuggestion"
            ></i>
          }
        </div>
        @if (tradeStudyToolFeaturesEnabled) {
          <!-- CapEx Equipment (0.9fr) -->
          <div
            class="flex items-center text-muted-color overflow-hidden min-w-0"
          >
            @if (children.length > 0) {
              <span class="text-xs text-muted-color">—</span>
            } @else {
              <p-multiSelect
                formControlName="capex_equipment_ids"
                [options]="equipmentOptions"
                optionLabel="name"
                optionValue="id"
                placeholder="Select"
                class="w-full"
                display="chip"
                appendTo="body"
                [maxSelectedLabels]="1"
                [selectedItemsLabel]="'{0} selected'"
                [pTooltip]="capexTooltip"
                tooltipPosition="top"
              />
            }
          </div>
        }
        <!-- CAD Link (40px) -->
        <div>
          <p-button
            [icon]="cadColumnIcon"
            text="true"
            styleClass="hover:!bg-primary-100"
            (onClick)="openCadLink.emit(item)"
            [pTooltip]="cadLinkTooltip"
            tooltipPosition="top"
            [badge]="linkedAssetBadge"
            [badgeSeverity]="'success'"
          >
          </p-button>
        </div>
        <!-- Actions (110px) -->
        <div class="flex justify-start">
          @if (children.length > 0) {
            <p-button
              text="true"
              severity="secondary"
              [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-left'"
              (onClick)="onToggleExpanded()"
            />
          }
          @if (consumablesEnabled && children.length === 0) {
            <app-item-consumables-editor
              [catalog]="consumableOptions"
              [selected]="item.consumables ?? []"
              [currencyCode]="currencyCode || 'GBP'"
              (selectedChange)="onConsumablesChange($event)"
            />
          }
          <p-button
            text="true"
            severity="secondary"
            icon="pi pi-ellipsis-v"
            (onClick)="actionMenu.toggle($event)"
          />
          <p-popover #actionMenu>
            <ng-template #content>
              <div class="flex flex-col gap-0 min-w-[160px]">
                <p-button
                  label="Add Child Item"
                  icon="pi pi-plus"
                  styleClass="w-full !justify-start"
                  [text]="true"
                  (onClick)="
                    onAddChildToThisItem(item.persistent_id); actionMenu.hide()
                  "
                />
                <p-button
                  [label]="
                    item.cad_object_ids.length > 0 ? 'Relink CAD' : 'Link CAD'
                  "
                  icon="pi pi-link"
                  styleClass="w-full !justify-start"
                  [text]="true"
                  [disabled]="!!item.drawing_file_id"
                  (onClick)="relinkCad.emit(item); actionMenu.hide()"
                />
                @if (item.cad_object_ids.length > 0) {
                  <p-button
                    label="Unlink CAD"
                    icon="pi pi-minus-circle"
                    styleClass="w-full !justify-start"
                    [text]="true"
                    (onClick)="
                      quoteItemDataEditorService.updateItem(item, {
                        cad_object_ids: [],
                        cad_object_names: [],
                      });
                      actionMenu.hide()
                    "
                  />
                }
                @if (item.drawing_file_id) {
                  <p-button
                    label="Unlink drawing PDF"
                    icon="pi pi-minus-circle"
                    styleClass="w-full !justify-start"
                    [text]="true"
                    (onClick)="
                      quoteItemDataEditorService.updateItem(item, {
                        drawing_file_id: null,
                        drawing_file_name: null,
                      });
                      actionMenu.hide()
                    "
                  />
                }
                <p-button
                  label="Delete"
                  icon="pi pi-trash"
                  styleClass="w-full !justify-start"
                  severity="danger"
                  [text]="true"
                  (onClick)="onRemoveItem(); actionMenu.hide()"
                />
              </div>
            </ng-template>
          </p-popover>
        </div>
      </div>
    </div>
    @if (expanded) {
      <div
        class="col-span-11 flex flex-col gap-1 pt-1 pb-2"
        [class.dragging-active]="dragging"
      >
        @for (child of children; track child.persistent_id) {
          <div
            pDroppable
            (drop)="quoteItemDataEditorService.drop(child, 'before')"
            class="drop-line ml-4"
          ></div>
          <div>
            <app-quote-item-data-tree
              [item]="child"
              [depth]="depth + 1"
              [currencyCode]="currencyCode || 'GBP'"
              [tradeStudyToolFeaturesEnabled]="tradeStudyToolFeaturesEnabled"
              [equipmentOptions]="equipmentOptions"
              [consumablesEnabled]="consumablesEnabled"
              [consumableOptions]="consumableOptions"
              (addChild)="onAddChildToThisItem($event)"
              (removeItem)="removeItem.emit($event)"
              (openCadLink)="openCadLink.emit($event)"
              (relinkCad)="relinkCad.emit($event)"
              (openPricingParameters)="openPricingParameters.emit($event)"
            />
          </div>
          @if ($last) {
            <div
              pDroppable
              (drop)="quoteItemDataEditorService.drop(child, 'endOfList')"
              class="drop-line ml-4"
            ></div>
          }
        } @empty {
          <div
            pDroppable
            (drop)="quoteItemDataEditorService.drop(item, 'child')"
            class="drop-line"
          ></div>
          <div class="text-muted-color text-sm italic px-4 py-2">
            No child items
          </div>
        }
      </div>
    }
  </div>
</form>
